import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import host from "../../global";
import { Send, MessageSquare, Award } from "lucide-react";
import { AiFillStar } from "react-icons/ai";
import BrandingFooter from "../../components/shared/BrandingFooter";
import { useSelector } from "react-redux";
import { useTheme } from "../../hooks/useTheme";

const ThankYou = () => {
  const { interview_id } = useParams();
  const navigate = useNavigate();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isWhiteLabelingEnable, setisWhiteLabelingEnable] = useState(
    JSON.parse(localStorage.getItem("isWhite")) || true
  );
  const [isFetched, setIsFetched] = useState(false);
  const [feedback, setFeedback] = useState({
    rating: 0,
    comfortLevel: 0,
    relevanceRating: 0,
    questionQuality: 0,
    difficultyLevel: 0,
    comments: "",
  });

  const logoPrimary = useSelector((state) => state.miscSlice.logoPrimary); 
  const { theme } = useTheme(); 

  useEffect(() => {
    const getStatus = async () => {
      const status = await JSON.parse(localStorage.getItem("isWhite"));
      setisWhiteLabelingEnable(status);
      setIsFetched(true);
    };

    getStatus();
  }, []);

  const handleRedirect = () => {
    setIsRedirecting(true);
    setTimeout(() => {
      navigate(`/candidate/${interview_id}/testhomepage`);
    }, 2000);
  };

  const handleRatingClick = (field, rating) => {
    setFeedback((prevFeedback) => ({
      ...prevFeedback,
      [field]: rating,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFeedback((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (document.fullscreenElement) exitFullscreen();

    try {
      const candidateToken = sessionStorage.getItem(`candidateToken_${interview_id}`);
      await axios.post(
        `${host}/submit_feedback?interview_id=${interview_id}`,
        feedback,
        { headers: candidateToken ? { Authorization: `Bearer ${candidateToken}` } : {} }
      );
      setFormSubmitted(true);
      handleRedirect();
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      alert("There was an error submitting your feedback. Please try again.");
    }
  };

  const handleSkipFeedback = () => {
    setFormSubmitted(true);
    handleRedirect();
  };

  const supportMailtoHref = `mailto:support@geniehire.ai?subject=${encodeURIComponent(
    `Interview Feedback - ${interview_id}`
  )}`;

  const renderRatingScale = (field) => {
    return (
      <div className="flex flex-col w-full">
        <div className="flex gap-5 w-full mb-2">
          {" "}
          {/* Minimal spacing */}
          {[1, 2, 3, 4, 5].map((rating) => (
            <button
              key={rating}
              type="button"
              onClick={() => handleRatingClick(field, rating)}
              className={`w-9 h-9 flex items-center justify-center transition-all duration-300 ease-in-out ${
                feedback[field] >= rating
                  ? "text-yellow-500"
                  : "text-gray-300 hover:text-yellow-300"
              }`}
              style={{ padding: 0, background: "none", border: "none" }}
            >
              <AiFillStar className="w-10 h-10" /> {/* Increased size */}
            </button>
          ))}
        </div>
      </div>
    );
  };

  if (formSubmitted) {
    return (
      <div
        className={`min-h-screen ${
          theme === 'light' ? "bg-[#FAF9F6]" : "app-background"
        } py-12 px-4 sm:px-6 lg:px-8 animate-gradient-x`}
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-center my-16 animate-fade-in">
            <div className="flex justify-center mb-6">
              <Award className="w-20 h-20 text-yellow-400 animate-pulse" />
            </div>
            <h1
              className={`text-6xl font-semibold tracking-tight ${
                theme === 'light'
                  ? "text-slate-900"
                  : "bg-gradient-to-r from-[#2960FD] to-[#FF60B6] text-transparent"
              } bg-clip-text mb-4`}
            >
              Thank You!
            </h1>
            <h3
              className={`text-2xl mb-6 ${
                theme === 'light' ? "text-gray-700" : "text-gray-200 "
              }`}
            >
              Your interview is complete.
            </h3>
            {isRedirecting && (
              <p className="text-gray-400 mb-4 animate-pulse">
                Redirecting to Home Page...
              </p>
            )}
            <p
              className={`text-sm mt-4 ${
                theme === "light" ? "text-gray-500" : "text-gray-400"
              }`}
            >
              Facing an issue? Contact us at{" "}
              <a
                href={supportMailtoHref}
                className="text-blue-400 font-medium hover:text-blue-300 underline cursor-pointer"
              >
                support@geniehire.ai
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!isFetched) {
    return <></>;
  }

  return (
    <div
      className={`min-h-screen py-6 pb-0 px-6 sm:px-8 lg:px-12 ${
        theme === 'light'
          ? "bg-[#FAF9F6]"
          : "app-background animate-gradient-x"
      }`}
    >
     <img src={logoPrimary} alt="Logo" className={`absolute ${theme === 'light' ? 'size-10' : 'h-6 '} right-6 object-contain`} />
      <div className="max-w-4xl mx-auto mb-4">
        <div className="text-center my-10 animate-fade-in">
          <div className="flex justify-center mb-6">
            <Award className="w-20 h-20 text-yellow-400 animate-pulse" />
          </div>
          <h1
            className={`text-4xl font-bold tracking-tight ${
              theme === 'light'
                ? "text-slate-900"
                : "bg-gradient-to-r from-[#2960FD] to-[#FF60B6] text-transparent"
            } bg-clip-text mb-4`}
          >
            Thank You!
          </h1>
          <h3
            className={`text-md mb-2 ${
              theme === 'light' ? "text-gray-700" : "text-gray-200 "
            }`}
          >
            Your interview is complete.
          </h3>
          <p className="text-gray-400 text-sm mb-2">
            Your feedback will help us improve. Please take a moment to share
            your thoughts.
          </p>
          <p className="text-gray-400 text-sm mb-14">
            Facing an issue? Contact us at{" "}
            <a
              href={supportMailtoHref}
              className="text-blue-400 font-medium hover:text-blue-300 underline cursor-pointer"
            >
              support@geniehire.ai
            </a>
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-10 animate-fade-in w-full mx-10 pl-32"
        >
          {/* Question 1 */}
          <div className="mb-8">
            <label
              className={`block font-lighttext-md mb-6 ${
                theme === 'light' ? "text-gray-700" : "text-gray-200 "
              }`}
            >
              1. How would you rate your experience with the AI Interviewer?
            </label>
            {renderRatingScale("rating", "Very Bad", "Fantastic")}
          </div>

          {/* Question 2 */}
          <div className="mb-8">
            <label
              className={`block font-lighttext-md mb-6 ${
                theme === 'light' ? "text-gray-700" : "text-gray-200 "
              }`}
            >
              2. How comfortable did you feel conversing with the AI
              Interviewer?
            </label>
            {renderRatingScale(
              "comfortLevel",
              "Very Uncomfortable",
              "Extremely Comfortable"
            )}
          </div>

          {/* Question 3 */}
          <div className="mb-8">
            <label
              className={`block font-lighttext-md mb-6 ${
                theme === 'light' ? "text-gray-700" : "text-gray-200 "
              }`}
            >
              3. Were the questions relevant to the job role you applied for?
            </label>
            {renderRatingScale(
              "relevanceRating",
              "Not Relevant",
              "Highly Relevant"
            )}
          </div>

          {/* Question 4 */}
          <div className="mb-8">
            <label
              className={`block font-lighttext-md mb-6 ${
                theme === 'light' ? "text-gray-700" : "text-gray-200 "
              }`}
            >
              4. How would you rate the quality of the questions and follow-up
              questions?
            </label>
            {renderRatingScale(
              "questionQuality",
              "Poor Quality",
              "Excellent Quality"
            )}
          </div>

          {/* Question 5 */}
          <div className="mb-8">
            <label
              className={`block font-lighttext-md mb-6 ${
                theme === 'light' ? "text-gray-700" : "text-gray-200 "
              }`}
            >
              5. How appropriate was the difficulty level of the questions
              asked?
            </label>
            {renderRatingScale("difficultyLevel", "Too Easy", "Too Difficult")}
          </div>

          {/* Question 6 */}
          {/* <div className="mb-8">
            <label className="block font-light text-gray-200 text-md mb-4">
              6. Were there any questions that seemed repetitive or unnecessary?
            </label>
            <textarea
              name="repetitiveQuestions"
              value={feedback.repetitiveQuestions}
              onChange={handleInputChange}
              placeholder="Please share your thoughts..."
              rows="3"
              className="w-full max-w-2xl px-4 py-3 rounded-lg bg-gray-800/50 text-sm text-gray-200 text-white placeholder:text-sm placeholder-gray-400 border border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all duration-200"
            />
          </div> */}

          {/* Question 7 */}
          {/* <div className="mb-8">
            <label className="block font-light text-gray-200 text-md mb-4">
              7. What improvements would make the AI interview experience better?
            </label>
            <textarea
              name="improvements"
              value={feedback.improvements}
              onChange={handleInputChange}
              placeholder="Share your suggestions..."
              rows="3"
              className="w-full max-w-2xl px-4 py-3 rounded-lg bg-gray-800/50 text-sm text-gray-200 text-white placeholder:text-sm placeholder-gray-400 border border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all duration-200"
            />
          </div> */}

          {/* Question 8 */}
          <div className="mb-8">
            <label
              className={`block font-lighttext-md mb-6 ${
                theme === 'light' ? "text-gray-700" : "text-gray-200 "
              }`}
            >
              6. Is there anything else you'd like to share about your interview
              experience?
            </label>
            <textarea
              name="comments"
              value={feedback.comments}
              onChange={handleInputChange}
              placeholder="Any additional thoughts..."
              rows="3"
              className={`
                w-full max-w-2xl px-4 py-3 rounded-lg text-sm placeholder:text-sm 
                transition-all duration-200 
                focus:outline-none
                ${
                  theme === 'light'
                    ? "bg-[#FAF9F6] text-gray-900 placeholder-gray-600 border border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600"
                    : "bg-gray-800/50 text-gray-200 placeholder-gray-400 border border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                }
              `}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-end mt-12 mb-16 pr-20">
            <button
              type="submit"
              className={`w-fit 
                text-sm group  font-normal transition-all duration-300 
               focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 
                flex items-center justify-center gap-2  px-6 py-2 rounded-full bg-gradient-to-r from-[#2960FD] to-[#FF60B6] text-white
              `}
            >
              {/* <span><Send className="w-5 h-5 group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform" /></span> */}
              <span>Submit Feedback</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setFormSubmitted(true);
                handleRedirect();
                if (document.fullscreenElement) exitFullscreen();
              }}
              className={`${theme === 'light' ? "border-btn" : "bg-gray-800 hover:bg-gray-700 text-gray-200 px-6 py-2"} text-sm group w-fit rounded-full font-medium shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 flex items-center justify-center gap-2 whitespace-normal`}
            >
              <span className="text-textSecondary">Skip Feedback</span>
            </button>
          </div>
        </form>
      </div>
       <BrandingFooter />
    </div>
  );
};

export default ThankYou;
